import React from 'react';
import { Box, Typography, IconButton, Collapse, Link, Tooltip } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * CrisisResourcesBanner — Adult-9 from
 * docs/adult-front-end-tests-review/08-remaining-work-plan.md
 *
 * Sticky banner that surfaces locale-aware crisis numbers (Samaritans /
 * NHS 111 / Pieta House) for the rest of a session in which suicidal
 * ideation has been disclosed (active or passive).
 *
 * Activation triggers (see server/services/crisisResourcesService.js):
 *   - PHQ-9 Q9 endorsement (any frequency)
 *   - Positive C-SSRS (items 3+)
 *   - escalationPending fired (any urgency)
 *   - adult-self-harm-suicidality(-v2) module consulted
 *
 * Behaviour: starts expanded, can be collapsed to a compact bar (still
 * visible — phone numbers tappable). Can be dismissed via the X (server
 * tracks dismissal but a more severe activation will re-show it).
 */
const CrisisResourcesBanner = ({ resources, locale, onDismiss, onCollapse, collapsed = false }) => {
  if (!resources) return null;

  const { primary, secondary, extra, label } = resources;

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        backgroundColor: '#fff4f4', // soft warning background, not alarming
        borderBottom: '2px solid #d32f2f',
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        px: 2,
        py: collapsed ? 0.5 : 1.25,
      }}
      role="region"
      aria-label="Crisis support resources"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <PhoneIcon sx={{ color: '#d32f2f', fontSize: collapsed ? 18 : 22 }} aria-hidden />
        <Typography
          variant={collapsed ? 'body2' : 'subtitle2'}
          sx={{ fontWeight: 600, color: '#5d1a1a' }}
        >
          Need to talk to someone?
        </Typography>

        {/* Compact line — always visible. On phones it takes its own line under the
            heading: squeezed beside it, the numbers broke word by word and the banner
            filled the screen even when minimised (ST-20261006-0003/0004). */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', columnGap: 1.5, rowGap: 0.25, flexWrap: 'wrap',
            order: { xs: 3, sm: 0 },
            flex: { xs: '1 0 100%', sm: 1 },
            ml: { xs: 0, sm: 0.5 }
          }}
        >
          <Link
            href={`tel:${primary.number.replace(/\s+/g, '')}`}
            sx={{ color: '#5d1a1a', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}
          >
            {primary.name} {primary.number}
          </Link>
          <Typography variant="body2" sx={{ color: '#5d1a1a', opacity: 0.7 }}>
            ·
          </Typography>
          <Link
            href={`tel:${secondary.number.replace(/\s+/g, '')}`}
            sx={{ color: '#5d1a1a', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}
          >
            {secondary.name} {secondary.number}
          </Link>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
          <Tooltip title={collapsed ? 'Show details' : 'Hide details'}>
            <IconButton size="small" onClick={onCollapse} aria-label="toggle details" sx={{ color: '#5d1a1a' }}>
              {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Dismiss for this session">
            <IconButton size="small" onClick={onDismiss} aria-label="dismiss banner" sx={{ color: '#5d1a1a' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Collapse in={!collapsed}>
        <Box sx={{ pt: 1, pl: { xs: 0, sm: 4 }, color: '#5d1a1a' }}>
          <Typography variant="body2" sx={{ display: 'block' }}>
            <strong>{primary.name}</strong>: <Link href={`tel:${primary.number.replace(/\s+/g, '')}`}>{primary.number}</Link> ({primary.detail})
          </Typography>
          <Typography variant="body2" sx={{ display: 'block', mt: 0.25 }}>
            <strong>{secondary.name}</strong>: <Link href={`tel:${secondary.number.replace(/\s+/g, '')}`}>{secondary.number}</Link> ({secondary.detail})
          </Typography>
          {extra && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.85 }}>
              {extra}
            </Typography>
          )}
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.7, fontStyle: 'italic' }}>
            If you are in immediate danger, please call 999 (UK) or 112 (Ireland).
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

export default CrisisResourcesBanner;
